/**
 * The shared visual primitives: company marks, badges, icons, and the small
 * pieces of chrome that appear on more than one page.
 */

import type { ReactNode } from 'react';

import type { Company, CompanyRef } from '@bidtohire/shared';

import { useAssetsBasePath } from '../lib/config';
import { assetUrl, initials, markColour } from '../lib/format';

/* -------------------------------------------------------------------------- */
/* Company mark                                                               */
/* -------------------------------------------------------------------------- */

export type LogoSize = 'sm' | 'md' | 'lg' | 'base';

const LOGO_CLASS: Record<LogoSize, string> = {
  sm: 'logo is-sm',
  base: 'logo',
  md: 'logo is-md',
  lg: 'logo is-lg',
};

export function CompanyMark({
  company,
  size = 'base',
  className,
  colour,
}: {
  readonly company: Pick<Company | CompanyRef, 'name' | 'slug'> & { readonly logoKey?: string };
  readonly size?: LogoSize;
  readonly className?: string;
  /**
   * Overrides the colour a logo-less mark would pick for itself. Only the
   * invented companies in the /about diagram use this, so a made-up board
   * still reads as a spread of marks rather than five of one colour.
   */
  readonly colour?: string;
}): ReactNode {
  const src = assetUrl(useAssetsBasePath(), company.logoKey);
  const classes = `${LOGO_CLASS[size]}${className === undefined ? '' : ` ${className}`}`;

  if (src !== undefined) {
    return <img className={classes} src={src} alt="" loading="lazy" />;
  }
  return (
    <span
      className={classes}
      style={{ background: colour ?? markColour(company.slug) }}
      aria-hidden="true"
    >
      {initials(company.name)}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* Chrome                                                                     */
/* -------------------------------------------------------------------------- */

export function Eyebrow({ children }: { readonly children: ReactNode }): ReactNode {
  return <span className="eyebrow">{children}</span>;
}

export function LiveDot({ indigo = false }: { readonly indigo?: boolean }): ReactNode {
  return <span className={indigo ? 'dot is-indigo' : 'dot'} aria-hidden="true" />;
}

/** The struck-through company tagline, rendered as real markup. */
export function MoneyWhereMouthIs(): ReactNode {
  return (
    <>
      Put your money where your <span className="struck">mouth</span> job is.
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Icons — inline, so the page never waits on a sprite                        */
/* -------------------------------------------------------------------------- */

export function BrandGlyph({ size = 20 }: { readonly size?: number }): ReactNode {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <rect x="1" y="11" width="4.4" height="8" rx="1.2" fill="#533afd" />
      <rect x="7.8" y="6.5" width="4.4" height="12.5" rx="1.2" fill="#533afd" opacity=".62" />
      <rect x="14.6" y="1" width="4.4" height="18" rx="1.2" fill="#533afd" opacity=".3" />
    </svg>
  );
}

export function ArrowRight({ colour = '#fff' }: { readonly colour?: string }): ReactNode {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path
        d="M2.5 6h7M6.4 3l3 3-3 3"
        stroke={colour}
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function ExternalArrow({ colour = '#fff' }: { readonly colour?: string }): ReactNode {
  return (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path
        d="M5 9 9.4 4.6M9.6 4.4H5.4M9.6 4.4v4.2"
        stroke={colour}
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** The small upward triangle beside the leader's rank. */
export function LeadCaret({ colour = '#1ea672' }: { readonly colour?: string }): ReactNode {
  return (
    <svg width="9" height="9" viewBox="0 0 10 10" fill="none" aria-hidden="true">
      <path d="M5 1.6 8.6 8H1.4L5 1.6Z" fill={colour} />
    </svg>
  );
}

export function CheckIcon({ colour = '#0e6245' }: { readonly colour?: string }): ReactNode {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M4 8.4 6.8 11.2 12 5.6"
        stroke={colour}
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function AlertIcon({ colour = '#983705' }: { readonly colour?: string }): ReactNode {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M8 4.6v4.2M8 11.2v.2" stroke={colour} strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

export function ShieldIcon(): ReactNode {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M8 1.6 14 4v4.2c0 3.3-2.5 5.4-6 6.2-3.5-.8-6-2.9-6-6.2V4l6-2.4Z"
        stroke="#64748d"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <path
        d="M5.6 8.1 7.3 9.8l3.2-3.3"
        stroke="#64748d"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function PlusIcon(): ReactNode {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="M7 1.8v10.4M1.8 7h10.4" stroke="#533afd" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

export function CloseIcon(): ReactNode {
  return (
    <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="M2.5 2.5l7 7M9.5 2.5l-7 7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

export function MenuIcon(): ReactNode {
  return (
    <svg width="19" height="19" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path
        d="M2.5 5h15M2.5 10h15M2.5 15h15"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
